import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { catalogApi, type ProductInput } from '@/api/catalogApi'
import { Page } from '@/components/layout/Page'
import { PageHeading } from '@/components/layout/PageHeading'
import { BackLink, ListState, StatusPill, SuperAdminGate, TabBar } from '@/components/admin/catalog/CatalogShared'
import { ComponentsPanel } from '@/components/admin/catalog/ComponentsPanel'
import { KeyPatternsPanel } from '@/components/admin/catalog/KeyPatternsPanel'
import { ProductForm } from '@/components/admin/catalog/ProductForm'
import { useCatalogMutation, useCatalogProduct } from '@/hooks/useCatalog'
import { useToast } from '@/providers/ToastProvider'

const TABS = ['details', 'components', 'patterns'] as const
type Tab = (typeof TABS)[number]

/** /admin/products/:id — Details | Components | Record key patterns. */
export default function CatalogProductPage() {
  return (
    <SuperAdminGate>
      <ProductDetail />
    </SuperAdminGate>
  )
}

function ProductDetail() {
  const { t } = useTranslation()
  const { id } = useParams()
  const [params, setParams] = useSearchParams()
  const product = useCatalogProduct(id)
  const { notify } = useToast()
  const update = useCatalogMutation((input: ProductInput) => catalogApi.updateProduct(id!, input))
  const tab: Tab = TABS.find((x) => x === params.get('tab')) ?? 'details'
  const p = product.data

  return (
    <Page>
      <BackLink to="/admin/products" />
      <PageHeading
        description={t('catalog.eyebrow')}
        title={p?.name ?? t('catalog.products.title')}
        actions={
          p ? (
            <div className="flex items-center gap-3">
              <span className="font-mono text-meta text-ink-muted">{p.key}</span>
              <span className="text-meta text-ink-muted">{t('catalog.products.ticketCount', { count: p._count.tickets })}</span>
              <StatusPill status={p.active ? 'active' : 'inactive'} />
            </div>
          ) : null
        }
      />
      <ListState query={product}>
        {p ? (
          <>
            <TabBar tabs={TABS} value={tab} labelKey="catalog.products.tabs" onChange={(x) => setParams(x === 'details' ? {} : { tab: x }, { replace: true })} />
            {tab === 'details' ? (
              <ProductForm
                key={`${p.id}:${p.key}:${p.name}:${p.active}:${p.slaTargetPct}:${p.description ?? ''}`}
                product={p}
                pending={update.isPending}
                error={update.error}
                onSubmit={(input) => update.mutate(input, { onSuccess: () => notify({ title: t('catalog.products.saved') }) })}
              />
            ) : tab === 'components' ? (
              <ComponentsPanel product={p} />
            ) : (
              <KeyPatternsPanel product={p} />
            )}
          </>
        ) : null}
      </ListState>
    </Page>
  )
}
